import { useEffect, useMemo, useState } from 'react';
import { Box, FormControl, InputLabel, MenuItem, Select, Switch, FormControlLabel } from '@mui/material';
import { TextField } from '../@core/components/mui';
import { PageFormLayout } from '../components/layouts/PageFormLayout';
import { settingsService } from '../services/settingsService';
import { useNotificationStore } from '../store/notificationStore';
import { useSettingsStore } from '../store/settingsStore';
import type { UpdateUserSettingsRequest, UserSettingsDto } from '../types/settings';

/**
 * User settings page.
 */
export const UserSettingsPage = () => {
  const { addNotification } = useNotificationStore();
  const { updateSettings: updateUiSettings } = useSettingsStore();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [data, setData] = useState<UserSettingsDto | null>(null);

  const initialForm = useMemo<UpdateUserSettingsRequest>(
    () => ({
      theme: 'system',
      language: 'en',
      timeZone: 'UTC',
      dateFormat: 'MM/dd/yyyy',
      timeFormat: '12h',
      emailNotificationsEnabled: true,
      pushNotificationsEnabled: true,
      notificationPreferencesJson: null,
      dashboardLayoutJson: null,
      preferencesJson: null,
    }),
    []
  );

  const [form, setForm] = useState<UpdateUserSettingsRequest>(initialForm);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const settings = await settingsService.getMyUserSettings();
      setData(settings);
      setForm({
        theme: settings.theme || 'system',
        language: settings.language || 'en',
        timeZone: settings.timeZone || 'UTC',
        dateFormat: settings.dateFormat || 'MM/dd/yyyy',
        timeFormat: settings.timeFormat || '12h',
        emailNotificationsEnabled: settings.emailNotificationsEnabled,
        pushNotificationsEnabled: settings.pushNotificationsEnabled,
        notificationPreferencesJson: settings.notificationPreferencesJson ?? null,
        dashboardLayoutJson: settings.dashboardLayoutJson ?? null,
        preferencesJson: settings.preferencesJson ?? null,
      });

      // Apply theme immediately to UI (best-effort).
      if (settings.theme === 'light' || settings.theme === 'dark' || settings.theme === 'system') {
        updateUiSettings({ mode: settings.theme });
      }
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load user settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
  }, []);

  const save = async () => {
    setSaving(true);
    setError(null);
    try {
      const updated = await settingsService.updateMyUserSettings(form);
      setData(updated);
      addNotification('User settings saved', 'success');

      if (updated.theme === 'light' || updated.theme === 'dark' || updated.theme === 'system') {
        updateUiSettings({ mode: updated.theme });
      }
    } catch (err: any) {
      const msg = err.response?.data?.message || err.message || 'Failed to save user settings';
      setError(msg);
      addNotification(msg, 'error');
    } finally {
      setSaving(false);
    }
  };

  return (
    <PageFormLayout
      title="User Settings"
      subtitle={data?.userId ? `User: ${data.userId}` : undefined}
      loading={loading}
      error={error || undefined}
      onErrorDismiss={() => setError(null)}
      actions={{
        onSave: save,
        onCancel: () => window.history.back(),
        saving,
      }}
    >
      <Box sx={{ display: 'grid', gap: 3, gridTemplateColumns: { xs: '1fr', md: 'repeat(2, 1fr)' } }}>
        <FormControl size="small">
          <InputLabel>Theme</InputLabel>
          <Select
            label="Theme"
            value={form.theme}
            onChange={(e) => setForm((s) => ({ ...s, theme: e.target.value }))}
            disabled={loading}
          >
            <MenuItem value="system">System</MenuItem>
            <MenuItem value="light">Light</MenuItem>
            <MenuItem value="dark">Dark</MenuItem>
          </Select>
        </FormControl>

        <TextField
          label="Language"
          size="small"
          value={form.language}
          onChange={(e) => setForm((s) => ({ ...s, language: e.target.value }))}
          disabled={loading}
        />

        <TextField
          label="Time Zone"
          size="small"
          value={form.timeZone}
          onChange={(e) => setForm((s) => ({ ...s, timeZone: e.target.value }))}
          disabled={loading}
        />

        <TextField
          label="Date Format"
          size="small"
          value={form.dateFormat}
          onChange={(e) => setForm((s) => ({ ...s, dateFormat: e.target.value }))}
          disabled={loading}
          helperText='Example: "MM/dd/yyyy"'
        />

        <FormControl size="small">
          <InputLabel>Time Format</InputLabel>
          <Select
            label="Time Format"
            value={form.timeFormat}
            onChange={(e) => setForm((s) => ({ ...s, timeFormat: e.target.value }))}
            disabled={loading}
          >
            <MenuItem value="12h">12-hour</MenuItem>
            <MenuItem value="24h">24-hour</MenuItem>
          </Select>
        </FormControl>

        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <FormControlLabel
            control={
              <Switch
                checked={form.emailNotificationsEnabled}
                onChange={(e) => setForm((s) => ({ ...s, emailNotificationsEnabled: e.target.checked }))}
              />
            }
            label="Email notifications"
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.pushNotificationsEnabled}
                onChange={(e) => setForm((s) => ({ ...s, pushNotificationsEnabled: e.target.checked }))}
              />
            }
            label="Push notifications"
          />
        </Box>
      </Box>
    </PageFormLayout>
  );
};
